import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { toggleSearch, closeSearch } from '../features/uiSlice';
import { useGetMaterialsQuery } from '../services/materialsApi';
import { getPosterUrl } from '../utils/poster';
import { trackEvent } from '../utils/analytics';
import styles from '../css/SearchOverlay.module.css';

const SearchOverlay = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isSearchOpen = useSelector((state) => state.ui.isSearchOpen);
  const inputRef = useRef(null);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  // Debounce 400ms
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), 400);
    return () => clearTimeout(timer);
  }, [query]);

  // Событие поиска вешаем на уже отдебаунсенный запрос, чтобы не слать
  // по одному событию на нажатие клавиши
  useEffect(() => {
    if (debouncedQuery.length < 2) return;
    trackEvent('search', { search_term: debouncedQuery });
  }, [debouncedQuery]);

  const { data, isFetching } = useGetMaterialsQuery(
    { search: debouncedQuery, per_page: 6, sort_by: 'imdb_rating', sort_order: 'desc' },
    { skip: debouncedQuery.length < 2 }
  );

  const results = data?.data ?? [];

  useEffect(() => {
    if (isSearchOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
    if (!isSearchOpen) {
      setQuery('');
      setDebouncedQuery('');
    }
  }, [isSearchOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isSearchOpen) {
        dispatch(closeSearch());
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isSearchOpen, dispatch]);

  const handleOverlayClick = (e) => {
    if (e.target.classList.contains(styles.searchOverlay)) {
      dispatch(closeSearch());
    }
  };

  const trackResultClick = (item, index, via) => {
    trackEvent('select_search_result', {
      search_term: debouncedQuery,
      item_id: item.id,
      item_name: item.title,
      index,
      via,
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && results.length > 0) {
      const first = results[0];
      const link = first.slug && first.slug_id
        ? `/dorama/${first.slug}--${first.slug_id}`
        : `/dorama/${first.id}`;
      trackResultClick(first, 0, 'enter');
      dispatch(closeSearch());
      navigate(link);
    }
  };

  const handleResultClick = (item, index) => {
    trackResultClick(item, index, 'click');
    dispatch(closeSearch());
  };

  return (
    <div
      className={`${styles.searchOverlay} ${isSearchOpen ? styles.active : ''}`}
      onClick={handleOverlayClick}
    >
      <div className={styles.searchContainer}>
        <div className={styles.searchBox}>
          <input
            ref={inputRef}
            type="text"
            className={styles.searchInput}
            placeholder="Поиск дорам..."
            aria-label="Поиск дорам"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <button
            className={styles.searchClose}
            onClick={() => dispatch(toggleSearch())}
            aria-label="Закрыть поиск"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {debouncedQuery.length >= 2 && (
          <div className={styles.searchResults}>
            {isFetching ? (
              <div className={styles.searchStatus}>Поиск...</div>
            ) : results.length > 0 ? (
              <>
                {results.map((item, index) => {
                  const link = item.slug && item.slug_id
                    ? `/dorama/${item.slug}--${item.slug_id}`
                    : `/dorama/${item.id}`;
                  const isNoPoster = !item.poster_url || item.poster_url.includes('no-poster');
                  return (
                    <Link
                      key={item.id}
                      to={link}
                      className={styles.resultItem}
                      onClick={() => handleResultClick(item, index)}
                    >
                      {!isNoPoster ? (
                        <div className={styles.resultPosterWrap}>
                          <div className={styles.resultPosterSkeleton} />
                          <img
                            src={getPosterUrl(item.poster_url)}
                            alt={item.title}
                            className={styles.resultPoster}
                            onLoad={(e) => e.currentTarget.classList.add(styles.loaded)}
                          />
                        </div>
                      ) : (
                        <div className={styles.resultNoPoster}>
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <rect x="3" y="3" width="18" height="18" rx="2"/>
                            <circle cx="8.5" cy="8.5" r="1.5"/>
                            <path d="M21 15l-5-5L5 21"/>
                          </svg>
                        </div>
                      )}
                      <div className={styles.resultInfo}>
                        <span className={styles.resultTitle}>{item.title}</span>
                        <span className={styles.resultMeta}>
                          {item.year} {item.genres?.slice(0, 2).join(', ')}
                        </span>
                      </div>
                      {item.imdb_rating > 0 && (
                        <span className={styles.resultRating}>
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="12" height="12">
                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                          </svg>
                          {item.imdb_rating}
                        </span>
                      )}
                    </Link>
                  );
                })}
                <div className={styles.searchHint}>Enter — перейти к первому результату</div>
              </>
            ) : (
              <div className={styles.searchStatus}>Ничего не найдено</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchOverlay;
